import { createAction, createReducer } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import {
  FETCH_APPLICATION_CONFIGS_SUCCESS,
  SHOW_OR_HIDE_APPBAR_MENU,
} from './actions';
import { isPlainObject, isString } from 'lodash';

export const BOOT = createAction<Record<string, any>>(
  'boot saga: sequence all the stuff needed to start the app'
);
export const BOOT_FINISHED = createAction('boot saga finished');

export interface AppBarState {
  isBooting: boolean;
  bootDidFinish: boolean;
  isAppBarMenuOpen: boolean;
  isApplicationConfigsLoaded: boolean;
  applicationConfigs?: {
    configType: string;
    configKey: string;
    value: string | null;
    valueJSON: Record<string, unknown> | null;
    defaultValue: string | null;
    defaultValueJSON: Record<string, unknown> | null;
  }[];
}

const defaultState: AppBarState = {
  isBooting: false,
  bootDidFinish: false,
  isAppBarMenuOpen: false,
  isApplicationConfigsLoaded: false,
};

const reducer = createReducer(defaultState, (builder) => {
  builder
    .addCase(BOOT, (state) => ({
      ...state,
      isBooting: true,
      bootDidFinish: false,
    }))
    .addCase(BOOT_FINISHED, (state) => ({
      ...state,
      isBooting: false,
      bootDidFinish: true,
    }))
    .addCase(SHOW_OR_HIDE_APPBAR_MENU, (state) => ({
      ...state,
      isAppBarMenuOpen: !state.isAppBarMenuOpen,
    }))
    .addCase(FETCH_APPLICATION_CONFIGS_SUCCESS, (state, action) => ({
      ...state,
      applicationConfigs: action.payload.applicationConfig.records,
      isApplicationConfigsLoaded: true,
    }));
});

export default reducer;
export const namespace = 'app';

export const boot = (options: Record<string, any> = {}) => BOOT(options);

export const bootFinished = () => BOOT_FINISHED();

const local = (state: any) => state[namespace] as AppBarState;

export const isBooting = (state: any) => local(state)?.isBooting;
export const bootDidFinish = (state: any) => local(state)?.bootDidFinish;
export const selectIsAppBarMenuOpen = (state: any) =>
  local(state)?.isAppBarMenuOpen;
export const selectIsApplicationConfigsLoaded = (state: any) =>
  local(state)?.isApplicationConfigsLoaded;
export const selectRawApplicationConfigs = (state: any) =>
  local(state)?.applicationConfigs;
export const selectApplicationConfigs = createSelector(
  selectRawApplicationConfigs,
  (rac) =>
    rac?.reduce<Record<string, unknown>>((acc, config) => {
      const value = appConfigValue(config);
      if (!value) {
        return acc;
      }
      return {
        ...acc,
        [config.configKey]: value,
      };
    }, {}) || {}
);

export function appConfigValue(
  config: NonNullable<ReturnType<typeof selectRawApplicationConfigs>>[0]
) {
  if (config.configType === 'JSON') {
    // Work around SDK incorrect usage of value rather than valueJSON for JSON config keys
    return getBrokenJSONAppConfigValue(config);
  } else if (config.configType === 'Boolean') {
    // for some reason value / defaultValue are saved as string !!!
    return (config.value || config.defaultValue) === 'true';
  } else if (config.configType === 'Integer') {
    // for some reason value / defaultValue are saved as string !!!
    return config.value !== null
      ? parseInt(config.value)
      : config.defaultValue !== null
        ? parseInt(config.defaultValue)
        : null;
  } else if (config.configType === 'Float') {
    // for some reason value / defaultValue are saved as string !!!
    return config.value !== null
      ? Number(config.value)
      : config.defaultValue !== null
        ? Number(config.defaultValue)
        : null;
  }
  // TODO need to add conversion for Date still
  else {
    return config.value || config.defaultValue;
  }
}

function getBrokenJSONAppConfigValue(
  appConfig: NonNullable<ReturnType<typeof selectRawApplicationConfigs>>[0]
) {
  // Default to valueJSON if it exists
  const valueJSON = appConfig.valueJSON;
  if (valueJSON && Object.keys(valueJSON).length > 0) {
    // for now do not count if it is an empty object
    return valueJSON;
  }

  // next check if there is "value" to handle platform bug - eventually we can get rid of this
  let rawValue = appConfig.value;

  // next check for defaultValueJSON
  if (
    !rawValue &&
    appConfig.defaultValueJSON &&
    Object.keys(appConfig.defaultValueJSON).length > 0
  ) {
    return appConfig.defaultValueJSON;
  }

  // as last resort check the defaultValue
  if (!rawValue) {
    rawValue = appConfig.defaultValue;
  }

  // try to parse value as JSON to handle platform UI deficiency
  if (isString(rawValue)) {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const value = JSON.parse(rawValue);
      if (isPlainObject(value)) {
        // Relatively safe cast...
        return value as Record<string, any>;
      } else {
        throw new Error('Invalid JSON structure in app config');
      }
    } catch (_e) {
      console.error('Invalid JSON string in app config:', rawValue);
      return undefined;
    }
  }
}
